import videocallshopApi from '../apis/videocallshop';

import { toastr } from 'react-redux-toastr';

import {
    CARTS_FETCH_REQUESTED,
    CARTS_FETCH_SUCCESS,
    CARTS_FETCH_FAILED,
    CART_FETCH_REQUESTED,
    CART_FETCH_SUCCESS,
    CART_FETCH_FAILED,
    CART_ITEM_ADD_REQUESTED,
    CART_ITEM_ADD_SUCCESS,
    CART_ITEM_ADD_FAILED,
    CART_ITEM_UPDATE_REQUESTED,
    CART_ITEM_UPDATE_SUCCESS,
    CART_ITEM_UPDATE_FAILED,
    CART_ITEM_REMOVE_REQUESTED,
    CART_ITEM_REMOVE_SUCCESS,
    CART_ITEM_REMOVE_FAILED,
    CART_CHECKOUT_REQUESTED,
    CART_CHECKOUT_SUCCESS,
    CART_CHECKOUT_FAILED,
    CART_CHECKOUT_CONFLICT,
    CART_NOTICE_DISMISSED,
} from './types';

// Contrato: openspec/changes/cart-and-checkout/design.md ("API contract").
// Todas las rutas van con x-customer-token (lo agrega el interceptor de
// apis/videocallshop.js). El carrito lo precia siempre la API: acá nunca se
// calcula un precio, sólo se muestra lo que vuelve.
const cartPath = (storeId) => `/customers/me/carts/${storeId}`;

// Mensaje de la API ({ message }) o del express-validator ({ errors: [...] }).
export const apiErrorMessage = (err, fallback) => {
    const body = err && err.response && err.response.data;
    if (body && body.message) return body.message;
    if (body && Array.isArray(body.errors) && body.errors.length > 0) {
        const first = body.errors[0];
        return first.msg && first.msg !== 'Invalid value'
            ? first.msg
            : fallback;
    }
    return fallback;
};

const statusOf = (err) => (err && err.response ? err.response.status : null);

// Resúmenes para el badge. Sin toastr: el badge es accesorio, si falla
// simplemente no se muestra.
export const fetchCarts = () => async (dispatch) => {
    dispatch({ type: CARTS_FETCH_REQUESTED });

    try {
        const { data } = await videocallshopApi.get('/customers/me/carts');

        dispatch({ type: CARTS_FETCH_SUCCESS, payload: data.data || [] });
    } catch (err) {
        console.error('Error al cargar los carritos:', err);
        dispatch({ type: CARTS_FETCH_FAILED });
    }
};

export const fetchCart = (storeId) => async (dispatch) => {
    dispatch({ type: CART_FETCH_REQUESTED, payload: { storeId } });

    try {
        const { data } = await videocallshopApi.get(cartPath(storeId));

        dispatch({
            type: CART_FETCH_SUCCESS,
            payload: { storeId, cart: data.data },
        });
    } catch (err) {
        console.error('Error al cargar el carrito:', err);
        dispatch({ type: CART_FETCH_FAILED, payload: { storeId } });

        toastr.error(
            'Error',
            apiErrorMessage(err, 'No se pudo cargar tu carrito.')
        );
    }
};

// Devuelve el Cart actualizado, o null si falló (el componente decide si
// muestra el aviso de "Agregado").
// origin: de dónde salió el agregado ('store_page', o 'live' / 'call' desde el
// carrusel de productos de una transmisión o una videollamada).
export const addToCart = (
    storeId,
    { productId, quantity = 1, selectedOptions = {}, origin = 'store_page' }
) => async (dispatch) => {
    dispatch({ type: CART_ITEM_ADD_REQUESTED, payload: { storeId } });

    try {
        const { data } = await videocallshopApi.post(
            `${cartPath(storeId)}/items`,
            { productId, quantity, selectedOptions, origin }
        );

        dispatch({
            type: CART_ITEM_ADD_SUCCESS,
            payload: { storeId, cart: data.data },
        });
        return data.data;
    } catch (err) {
        dispatch({ type: CART_ITEM_ADD_FAILED, payload: { storeId } });

        // 422 = selección inválida ("Elegí Talle."), 404 = ya no se vende
        const status = statusOf(err);
        if (status === 422 || status === 404) {
            toastr.warning(
                'No se pudo agregar',
                apiErrorMessage(err, 'Revisá las opciones del producto.')
            );
        } else {
            console.error('Error al agregar al carrito:', err);
            toastr.error(
                'Error',
                apiErrorMessage(err, 'No se pudo agregar al carrito.')
            );
        }
        return null;
    }
};

export const updateCartItem = (storeId, cartItemId, quantity) => async (
    dispatch
) => {
    dispatch({
        type: CART_ITEM_UPDATE_REQUESTED,
        payload: { storeId, cartItemId },
    });

    try {
        const { data } = await videocallshopApi.patch(
            `${cartPath(storeId)}/items/${cartItemId}`,
            { quantity }
        );

        dispatch({
            type: CART_ITEM_UPDATE_SUCCESS,
            payload: { storeId, cartItemId, cart: data.data },
        });
    } catch (err) {
        console.error('Error al cambiar la cantidad:', err);
        dispatch({
            type: CART_ITEM_UPDATE_FAILED,
            payload: { storeId, cartItemId },
        });

        toastr.error(
            'Error',
            apiErrorMessage(err, 'No se pudo cambiar la cantidad.')
        );
        // la línea pudo haber desaparecido (404): releemos el carrito
        if (statusOf(err) === 404) dispatch(fetchCart(storeId));
    }
};

export const removeCartItem = (storeId, cartItemId) => async (dispatch) => {
    dispatch({
        type: CART_ITEM_REMOVE_REQUESTED,
        payload: { storeId, cartItemId },
    });

    try {
        const { data } = await videocallshopApi.delete(
            `${cartPath(storeId)}/items/${cartItemId}`
        );

        dispatch({
            type: CART_ITEM_REMOVE_SUCCESS,
            payload: { storeId, cartItemId, cart: data.data },
        });
    } catch (err) {
        console.error('Error al quitar el producto:', err);
        dispatch({
            type: CART_ITEM_REMOVE_FAILED,
            payload: { storeId, cartItemId },
        });

        toastr.error(
            'Error',
            apiErrorMessage(err, 'No se pudo quitar el producto.')
        );
        if (statusOf(err) === 404) dispatch(fetchCart(storeId));
    }
};

// Resultado para el componente:
//   { ok: true, result }            -> redirigir a result.paymentLink
//   { conflict: true, code, message, shippingQuote }
//        el carrito cambió. Con code 'cart_total_changed' el checkout se queda
//        (muestra el total nuevo y la cotización nueva); si no, vuelve al
//        carrito.
//   { code: 'customer_email_required' | 'invalid_address', message }
//   { failed: true }
//
// Envío a domicilio (addendum 2026-09-30): shippingOptionId 3 + address, y
// expectedTotal = subtotal + envío. Retiro: shippingOptionId 1.
export const checkoutCart = (
    storeId,
    // callRequestId: checkout desde adentro de una videollamada 1:1; la API
    // vincula el pedido a esa llamada.
    { paymentOptionId, expectedTotal, shippingOptionId, address, callRequestId }
) => async (dispatch) => {
    dispatch({ type: CART_CHECKOUT_REQUESTED, payload: { storeId } });

    try {
        const { data } = await videocallshopApi.post(
            `${cartPath(storeId)}/checkout`,
            { paymentOptionId, expectedTotal, shippingOptionId, address, callRequestId }
        );

        dispatch({
            type: CART_CHECKOUT_SUCCESS,
            payload: { storeId, result: data.data },
        });
        return { ok: true, result: data.data };
    } catch (err) {
        const status = statusOf(err);
        const body = (err && err.response && err.response.data) || {};
        const message = apiErrorMessage(err, 'No se pudo iniciar el pago.');

        // 409 (precio/stock/envío cambió) o 400 carrito vacío: traen el
        // carrito fresco en `data`, que reemplaza al local. Con envío a
        // domicilio, `data.shippingQuote` es la cotización nueva.
        if (
            (status === 409 || status === 400) &&
            body.data &&
            body.data.items
        ) {
            const { shippingQuote = null, ...cart } = body.data;
            const stays = body.code === 'cart_total_changed';
            dispatch({
                type: CART_CHECKOUT_CONFLICT,
                // el checkout muestra su propio aviso; el del carrito es
                // para cuando se vuelve ahí
                payload: { storeId, cart, message: stays ? null : message },
            });
            if (!stays) toastr.warning('Tu carrito cambió', message);
            return { conflict: true, code: body.code, message, shippingQuote };
        }

        dispatch({ type: CART_CHECKOUT_FAILED, payload: { storeId } });

        if (
            body.code === 'customer_email_required' ||
            body.code === 'invalid_address'
        ) {
            return { code: body.code, message };
        }

        console.error('Error en el checkout:', err);
        toastr.error('No se pudo iniciar el pago', message);
        return { failed: true, message };
    }
};

// Envío a domicilio. Sin redux ni toastr: el formulario de la dirección
// muestra el error en su lugar. Tiran un Error con el mensaje de la API (el
// 429 del rate limit incluido) y `status`/`code`.
const requestError = (err, fallback) => {
    const body = (err && err.response && err.response.data) || {};
    const error = new Error(
        statusOf(err) === 429
            ? body.message ||
              'Demasiados pedidos seguidos. Esperá un momento y probá de nuevo.'
            : apiErrorMessage(err, fallback)
    );
    error.status = statusOf(err);
    error.code = body.code || null;
    return error;
};

// { shippingOptionId: 3, price, productName, postalCode, validTo, subtotal, total }
export const quoteCartShipping = async (storeId, postalCode) => {
    try {
        const { data } = await videocallshopApi.post(
            `${cartPath(storeId)}/shipping/quote`,
            { postalCode }
        );
        return data.data;
    } catch (err) {
        throw requestError(err, 'No pudimos cotizar el envío.');
    }
};

// [{ placeId, label, mainText, secondaryText }]
export const autocompleteAddress = async (input, sessionToken) => {
    try {
        const {
            data,
        } = await videocallshopApi.get('/customers/me/address/autocomplete', {
            params: { input, sessionToken },
        });
        return data.data || [];
    } catch (err) {
        throw requestError(err, 'No pudimos buscar la dirección.');
    }
};

// { formattedAddress, streetName, streetNumber, locality, provinceName,
//   provinceCode, postalCode }
export const addressDetails = async (placeId, sessionToken) => {
    try {
        const {
            data,
        } = await videocallshopApi.get(
            `/customers/me/address/details/${encodeURIComponent(placeId)}`,
            { params: { sessionToken } }
        );
        return data.data;
    } catch (err) {
        throw requestError(err, 'No pudimos leer esa dirección.');
    }
};

export const dismissCartNotice = (storeId) => ({
    type: CART_NOTICE_DISMISSED,
    payload: { storeId },
});
